/**
 * 右栏「属性」的数据源 —— 从真实 DOM 读出选中元素的当前值。
 *
 * 读取顺序：**属性优先，其次内联 style**（外部 SVG 常用 style="fill:#f00"，
 * 只读属性会得到 null 而让面板显示空白）。写回时的对应处理见 ops/style.ts。
 * 位置/尺寸需要真实布局（getBBox），jsdom 下没有 → 一律 null，浏览器里才有效。
 */
import type { Rect } from '../geom/matrix';
import { parseTransformList, rotationOf } from '../geom/transform';
import { parseStyleAttr, styleValue } from '../style-attr';

export type DashKind = 'solid' | 'dashed' | 'dotted' | 'dashdot';

export const DASH_PATTERN: Record<DashKind, string | null> = {
  solid: null,
  dashed: '6 4',
  dotted: '1 4',
  dashdot: '10 3 1 3',
};

export const DASH_LABEL: Record<DashKind, string> = {
  solid: '实线',
  dashed: '虚线',
  dotted: '点线',
  dashdot: '点划线',
};

/** dasharray 值 → 三档预设中的哪一档（未设置/ none 视为实线） */
export function dashKindOf(value: string | null | undefined): DashKind {
  if (!value || value === 'none') return 'solid';
  const nums = value
    .split(/[\s,]+/)
    .filter(Boolean)
    .map(Number)
    .filter((n) => Number.isFinite(n));
  if (nums.length === 0) return 'solid';
  if (nums.length >= 4) return 'dashdot';
  if ((nums[0] as number) <= 2) return 'dotted'; // 第一段（点）很短 = 点线
  return 'dashed';
}

const num = (v: string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * 读呈现值：**内联 style 优先于呈现属性**。
 * 依据 CSS 规范：呈现属性相当于优先级最低的样式表，而 style 永远胜出 ——
 * 顺序搞反的后果是"面板显示的是渲染结果里根本没生效的那个值"。
 */
export function readPresentation(el: Element, name: string): string | null {
  const styled = styleValue(el.getAttribute('style'), name);
  if (styled !== null && styled !== '') return styled;
  const attr = el.getAttribute(name);
  return attr !== null && attr !== '' ? attr : null;
}

/** 该元素内联样式里有哪些声明（调试与面板提示用） */
export const inlineStyles = (el: Element): Array<[string, string]> =>
  parseStyleAttr(el.getAttribute('style'));

export interface PropsSnapshot {
  /** 选中数量；> 1 时只有 opacity / 填充这类可批量改的项有意义 */
  count: number;
  tag: string | null;
  id: string | null;
  text: string | null;
  fill: string | null;
  stroke: string | null;
  strokeWidth: number | null;
  dash: DashKind | null;
  fontSize: number | null;
  fontFamily: string | null;
  opacity: number;
  /** display:none（图层的"眼睛"关了） */
  hidden: boolean;
  /** rect 的圆角半径（rx） */
  radius: number | null;
  /** 顺时针角度（deg），从 transform 反推 */
  rotation: number;
  /** 需要真实布局；jsdom 下为 null */
  box: Rect | null;
  /** 元素支持哪些编辑项（UI 据此禁用无关分区） */
  supports: { fill: boolean; stroke: boolean; text: boolean; geometry: boolean; corner: boolean };
}

const FILLABLE = new Set(['rect', 'circle', 'ellipse', 'polygon', 'path', 'polyline', 'line', 'text', 'tspan', 'g', 'image', 'use']);
const TEXTUAL = new Set(['text', 'tspan']);
const GEOMETRIC = new Set(['rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'path', 'image', 'use', 'g', 'text']);

export function readProps(selection: Iterable<Element>): PropsSnapshot {
  const els = [...selection];
  const el = els[0] ?? null;
  const tag = el?.tagName.toLowerCase() ?? null;

  let box: Rect | null = null;
  if (el) {
    try {
      const b = (el as SVGGraphicsElement).getBBox();
      box = { x: b.x, y: b.y, w: b.width, h: b.height };
    } catch {
      box = null; // jsdom / 特殊元素：没有布局信息就老实返回 null
    }
  }

  return {
    count: els.length,
    tag,
    id: el?.getAttribute('id') ?? null,
    text: tag && TEXTUAL.has(tag) ? (el?.textContent ?? '') : null,
    fill: el ? readPresentation(el, 'fill') : null,
    stroke: el ? readPresentation(el, 'stroke') : null,
    strokeWidth: el ? num(readPresentation(el, 'stroke-width')) : null,
    dash: el ? dashKindOf(readPresentation(el, 'stroke-dasharray')) : null,
    fontSize: el ? num(readPresentation(el, 'font-size')) : null,
    fontFamily: el ? readPresentation(el, 'font-family') : null,
    opacity: el ? (num(readPresentation(el, 'opacity')) ?? 1) : 1,
    hidden: el ? readPresentation(el, 'display') === 'none' : false,
    radius: el ? num(readPresentation(el, 'rx')) : null,
    rotation: el ? rotationOf(parseTransformList(el.getAttribute('transform')).mat) : 0,
    box,
    supports: {
      fill: tag !== null && FILLABLE.has(tag),
      stroke: tag !== null && FILLABLE.has(tag),
      text: tag !== null && TEXTUAL.has(tag),
      geometry: tag !== null && GEOMETRIC.has(tag),
      corner: tag === 'rect',
    },
  };
}
